import { useNavigate } from 'react-router-dom';

import { useCompleteOnboarding, useOnboardingProfile } from '../../hooks/useOnboarding.js';
import { toast } from '../../lib/toast.js';

export function SummaryScreen() {
  const navigate = useNavigate();
  const profile = useOnboardingProfile();
  const complete = useCompleteOnboarding();

  if (profile.isLoading || !profile.data) {
    return (
      <section className="onboarding-screen">
        <div className="onboarding-card" role="status">
          Loading your summary…
        </div>
      </section>
    );
  }

  const data = profile.data;
  const modes = data.trackingModes ?? ['nutrition', 'training'];
  const nutrition = modes.includes('nutrition');
  const training = modes.includes('training');

  async function finish() {
    try {
      await complete.mutateAsync();
      navigate(training ? '/routines' : '/dashboard', { replace: true });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not finish setup');
    }
  }

  return (
    <section className="onboarding-screen">
      <div className="onboarding-card">
        <h2 className="onboarding-title">Your tracker is ready</h2>
        <p className="onboarding-subtitle">
          These settings create your deterministic starting point. No AI is required.
        </p>
        <div className="summary-sections">
          <SummaryRow
            title="Tracking"
            body={[nutrition ? 'Nutrition' : null, training ? 'Training' : null]
              .filter(Boolean)
              .join(' + ')}
          />
          <SummaryRow
            title="Goal"
            body={`${formatGoal(data.goalType)}${
              data.targetWeightKg != null ? ` · ${data.targetWeightKg} kg` : ''
            } by ${data.targetDate ? new Date(data.targetDate).toLocaleDateString() : 'your chosen date'}`}
          />
          {nutrition && (
            <SummaryRow
              title="Nutrition targets"
              body="Calories, protein, carbohydrates, and fat will be shown as ranges calculated from your saved inputs."
            />
          )}
          {training && (
            <SummaryRow
              title="Training"
              body={`${data.daysPerWeek ?? 0} days per week. Next, choose a template or build your own routine.`}
            />
          )}
        </div>
      </div>
      <div className="onboarding-actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => navigate('/onboarding/training')}
        >
          Back
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={finish}
          disabled={complete.isPending}
        >
          {complete.isPending ? 'Saving targets…' : 'Start tracking'}
        </button>
      </div>
    </section>
  );
}

function SummaryRow({ title, body }: { title: string; body: string }) {
  return (
    <section className="summary-section">
      <h3 className="summary-section-title">{title}</h3>
      <p className="summary-section-body">{body}</p>
    </section>
  );
}

function formatGoal(goal: string | null): string {
  if (!goal) return 'General fitness';
  return goal.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}
